/** @jsxImportSource @emotion/react */

import { RegisteredUserObject } from "@a_team/models/dist/UserObject";
import { CollaboratorAction } from "components/Collaborators/CollaboratorAction";
import { Flags } from "configs/featureFlags";
import { Role, rolesInOrderOfHierarchy } from "configs/role";
import useCanUpdateWorkspace from "hooks/access/useCanUpdateWorkspace";
import useUserRoleFromAccount from "hooks/access/useUserRoleFromAccount";
import useUserAccessLevelFromAccount from "hooks/access/userUserAccessLevelFromAccount";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { AccountMember, AccountRoleAccessLevel } from "models/Account";

// Used for when ClientRolesAndPerms feature flag is off
// Determines whether to hide the remove button for a collaborators
const legacyShouldHideActions = ({
  collaborator,
  currentUser,
}: {
  collaborator: AccountMember;
  currentUser?: RegisteredUserObject;
}): boolean => {
  const userIsSelf = currentUser?.uid === collaborator?.user.id;

  const teamAdvisorAccess = [
    AccountRoleAccessLevel.BDAdmin,
    AccountRoleAccessLevel.BDManager,
  ];

  const currentUserAccessLevel = useUserAccessLevelFromAccount(
    {}
  ) as AccountRoleAccessLevel;
  const currentUserIsTA = teamAdvisorAccess.includes(currentUserAccessLevel);
  const collaboratorIsTA = teamAdvisorAccess.includes(collaborator.accessLevel);

  if (userIsSelf) {
    return true;
  }

  // Team Advisors can remove anyone
  // but no one can remove a Team Advisor if they are not one
  return currentUserIsTA ? false : collaboratorIsTA;
};

export const RoleActionForCollaborator = ({
  collaborator,
  currentUser,
  isMissionContext,
}: {
  collaborator: AccountMember;
  currentUser?: RegisteredUserObject;
  isMissionContext: boolean;
}) => {
  const { flagEnabled: clientRolesAndPermsEnabled } = useFeatureFlag(
    Flags.ClientRolesAndPerms
  );

  const canUpdateWorkspace = useCanUpdateWorkspace();
  const userRole = useUserRoleFromAccount({ userId: collaborator.user.id });
  const currentUserRole = useUserRoleFromAccount({ userId: currentUser?.uid });
  const getRoleIdx = (role: Role) => rolesInOrderOfHierarchy.indexOf(role);
  const currentUserRoleIdx = getRoleIdx(currentUserRole as Role);
  const userRoleIdx = getRoleIdx(userRole as Role);
  // Indicates if the current user has the same or higher role than the user being viewed
  const currentUserIsEqualOrHigherRole =
    clientRolesAndPermsEnabled && currentUserRoleIdx <= userRoleIdx;

  // For legacy purposes - used in the case that ClientRolesAndPerms feature flag is off
  const hideActionsLegacy = legacyShouldHideActions({
    collaborator,
    currentUser,
  });

  // For backwards compatibility when ClientRolesAndPerms feature flag is off,
  if (!clientRolesAndPermsEnabled) {
    if (hideActionsLegacy) {
      return null;
    }
  }

  if (clientRolesAndPermsEnabled) {
    // Users cannot remove themselves
    if (currentUser?.uid === collaborator.user.id) {
      return null;
    }
    if (!currentUserIsEqualOrHigherRole) {
      return null;
    }
    // Workspace Collaborator Settings
    if (!isMissionContext) {
      if (!canUpdateWorkspace) {
        return null;
      }
    }
    // Mission Colaborator Settings
    if (isMissionContext) {
      if ([Role.TeamAdvisor, Role.WorkspaceAdmin].includes(userRole as Role)) {
        return null;
      }
    }
  }

  return <CollaboratorAction collaborator={collaborator} />;
};
